"use client";
import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay, Controller, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "swiper/css/controller";
import { Link } from "react-router-dom";
import { useTourismData } from "../../context/TourismContext";
import { getImageUrl } from "../../services/api";
import SubHeroSlider from "./SubHeroSlider";

function BannerThree() {
    const [mainSwiper, setMainSwiper] = useState(null);
    
    // Get tourism data and actions from context
    const { airTicket, loading, fetchAirTicketHeroSlides } = useTourismData();
    const { heroSlides } = airTicket;

    useEffect(() => {
        // Fetch AirTicket hero slides data
        const fetchData = async () => {
            try {
                await fetchAirTicketHeroSlides();
            } catch (error) {
                console.error('Error fetching hero slides:', error);
            }
        };
        
        fetchData();
        
        // Function to add animation classes
        const animationProperties = () => {
            document.querySelectorAll('[data-ani]').forEach((element) => {
                const animationName = element.getAttribute('data-ani');
                element.classList.add(animationName);
            });

            document.querySelectorAll('[data-ani-delay]').forEach((element) => {
                const delayTime = element.getAttribute('data-ani-delay');
                element.style.animationDelay = delayTime;
            });
        };

        animationProperties();
    }, [fetchAirTicketHeroSlides]);
    const handleScroll = (e) => {
        e.preventDefault();
        document.getElementById("destination-sec")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div className="hero-3" id="hero">
            {/* Main Swiper */}
            <Swiper
                modules={[EffectFade, Autoplay, Controller, Navigation, Pagination]}
                loop={true}
                effect="fade"
                autoplay={{ delay: 6000, disableOnInteraction: false }}
                pagination={{
                    el: ".slider-pagination",
                    type: "fraction",
                    renderFraction: (currentClass, totalClass) =>
                        `<span className="${currentClass}"></span> / <span className="${totalClass}"></span>`,
                }}
                navigation={{
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                }}
                // No thumbs swiper needed
                onSwiper={setMainSwiper}
                className="hero-slider-3"
            >
                {/* Image Slides */}
                {loading.airTicketHeroSlides ? (
                    // Loading placeholder
                    <SwiperSlide>
                        <div className="hero-inner">
                            <div className="th-hero-bg" style={{ backgroundColor: '#f5f5f5', minHeight: '500px' }}></div>
                            <div className="container">
                                <div className="hero-style3">
                                    <h1 className="hero-title" data-ani="slideinleft" data-ani-delay="0.2s">Loading...</h1>
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>
                ) : heroSlides && heroSlides.length > 0 ? (
                    // API data
                    heroSlides.map((slide, index) => (
                        <SwiperSlide key={slide.id || index}>
                            <div className="hero-inner">
                                {slide.slide_type === 'video' && slide.background_video_url ? (
                                    // Video Background
                                    <video autoPlay loop muted playsInline className="hero-video">
                                        <source src={getImageUrl(slide.background_video)} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                ) : (
                                    // Image Background
                                    <div className="th-hero-bg" style={{ 
                                        backgroundImage: `url(${getImageUrl(slide.background_image)})`, 
                                        backgroundRepeat: "no-repeat", 
                                        backgroundSize: "cover" 
                                    }}></div>
                                )}
                                <div className="container">
                                    <div className="hero-style3">
                                        <h1 className="hero-title" data-ani="slideinleft" data-ani-delay="0.2s">{slide.title}</h1>
                                        {slide.subtitle && (
                                            <h3 className="hero-subtitle" data-ani="slideinleft" data-ani-delay="0.3s">{slide.subtitle}</h3>
                                        )}
                                        <p className="hero-text" data-ani="slideinleft" data-ani-delay="0.4s">
                                            {slide.description}
                                        </p>
                                        {slide.primary_button_text && (
                                            <div className="btn-group" data-ani="slideinup" data-ani-delay="0.6s">
                                                <Link to={slide.primary_button_link || "/tour"} className="th-btn style2 th-icon">
                                                    {slide.primary_button_text}
                                                </Link>
                                                {slide.secondary_button_text && (
                                                    <Link to={slide.secondary_button_link || "/service"} className="th-btn style3 th-icon">
                                                        {slide.secondary_button_text}
                                                    </Link>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    ))
                ) : (
                    // Fallback static content if API fails
                    [
                        { img: "/assets/img/hero/hero_bg_3_1.jpg", title: "Discover The World with Our Guide" },
                        { img: "/assets/img/hero/hero_bg_3_2.jpg", title: "Discover The World Best Destination" },
                    ].map((slide, index) => (
                        <SwiperSlide key={index}>
                            <div className="hero-inner">
                                <div className="th-hero-bg" style={{ backgroundImage: `url(${slide.img})`, backgroundRepeat: "no-repeat", backgroundSize: "cover" }}></div>
                                <div className="container">
                                    <div className="hero-style3">
                                        <h1 className="hero-title" data-ani="slideinleft" data-ani-delay="0.2s">{slide.title}</h1>
                                        <p className="hero-text" data-ani="slideinleft" data-ani-delay="0.4s">
                                            Tourm is an international travel management company with 25 years of experience, specializing in business and maritime travel.
                                        </p>
                                        <div className="btn-group" data-ani="slideinup" data-ani-delay="0.6s">
                                            <Link to="/tour" className="th-btn style2 th-icon">
                                                Explore Tours
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </SwiperSlide>
                    ))
                )}

                {/* Video Slide */}
                <SwiperSlide>
                    <div className="hero-inner">
                        {/* Video Background */}
                        <video autoPlay loop muted playsInline className="hero-video">
                            <source src="/assets/img/hero/hero-video3.mp4" type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>

                        <div className="container">
                            <div className="hero-style3">
                                <h1 className="hero-title" data-ani="slideinleft" data-ani-delay="0.2s">
                                    Travel experience with Tourm
                                </h1>
                                <p className="hero-text" data-ani="slideinleft" data-ani-delay="0.4s">
                                    Tourm is an international travel management company with 25 years of
                                    experience, specializing in business and maritime travel.
                                </p>
                                <div className="btn-group" data-ani="slideinup" data-ani-delay="0.6s">
                                    <Link to="/tour" className="th-btn style2 th-icon">Explore Tours</Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </SwiperSlide>
            </Swiper>

            {/* Booking Form Section */}
            <SubHeroSlider />
            <div className="scroll-down">
                <Link to="/#destination-sec" onClick={handleScroll} className="scroll-wrap">
                    <span>
                        <img src="/assets/img/icon/down-arrow.svg" alt="" />
                    </span>
                    Scroll Down
                </Link>
            </div>
        </div>
    );
}

export default BannerThree;
